'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';

import type { ProviderMarker } from '@paction-2.0/ui';
import { getProviderProfileUrl } from '@paction-2.0/util';

import type {
  CatalogProviderLoaderResult,
  CatalogProviderMarker,
  CatalogProviderSummary,
  NormalizedCatalogSearchFilters
} from '@/features/catalog';

import { usePuppySearch } from '../usePuppySearch';
import { usePuppySearchNavigation } from '../usePuppySearchNavigation';
import { useProviderSelection } from '../useProviderSelection';

const DEFAULT_MARKER_ZOOM = 4;
const HEADER_EXPANDED_KEY = 'paction:searchHeaderExpanded';

type ProviderSearchMarker = ProviderMarker & {
  providerId: number;
  providerSlug: string;
  provinceSlug: string | null;
};

const hasProviderId = (
  marker: ProviderMarker
): marker is ProviderSearchMarker =>
  typeof (marker as { providerId?: unknown }).providerId === 'number';

const getInitialHeaderExpanded = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return sessionStorage.getItem(HEADER_EXPANDED_KEY) === '1';
  } catch {
    return false;
  }
};

export type UsePuppySearchExperienceArgs = {
  initialFilters: NormalizedCatalogSearchFilters;
  initialProviders: CatalogProviderSummary[];
  initialPagination: CatalogProviderLoaderResult['pagination'];
  /**
   * All providers matching the current filter, supplied by the server-side
   * `/catalog/providers/markers` endpoint. Independent of pagination — the
   * map shows every match even when the result list is on page 1. Optional
   * for backwards-compat with surfaces that haven't been wired up yet; in
   * that case we fall back to deriving markers from the loaded providers.
   */
  initialMarkers?: CatalogProviderMarker[];
};

export function usePuppySearchExperience({
  initialFilters,
  initialProviders,
  initialPagination,
  initialMarkers
}: UsePuppySearchExperienceArgs) {
  const router = useRouter();
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);

  // Sync with sessionStorage on mount
  useEffect(() => {
    setIsHeaderExpanded(getInitialHeaderExpanded());
  }, []);

  // Persist to sessionStorage when state changes
  const setHeaderExpanded = useCallback((expanded: boolean) => {
    setIsHeaderExpanded(expanded);
    try {
      if (expanded) {
        sessionStorage.setItem(HEADER_EXPANDED_KEY, '1');
      } else {
        sessionStorage.removeItem(HEADER_EXPANDED_KEY);
      }
    } catch {
      // Ignore storage errors
    }
  }, []);

  const search = usePuppySearch({
    initialFilters,
    initialProviders,
    initialPagination
  });

  const { applyAndNavigate } = usePuppySearchNavigation({ search });

  // Markers come from the server (one per matching provider, irrespective of
  // pagination). When the server payload is unavailable, fall back to the
  // current page of providers so the map still has pins to render.
  const providerMarkers = useMemo<ProviderSearchMarker[]>(() => {
    const source = initialMarkers ?? [];
    if (source.length > 0) {
      return source
        .filter(
          (m) =>
            typeof m.latitude === 'number' && typeof m.longitude === 'number'
        )
        .map((m) => ({
          providerId: m.id,
          providerSlug: m.slug,
          provinceSlug: m.provinceSlug,
          geo: { coordinates: [m.longitude, m.latitude] as [number, number] }
        }));
    }

    return search.providers
      .filter(
        (p) => typeof p.longitude === 'number' && typeof p.latitude === 'number'
      )
      .map((p) => ({
        providerId: p.id,
        providerSlug: p.slug,
        provinceSlug: p.provinceSlug ?? null,
        geo: {
          coordinates: [p.longitude as number, p.latitude as number] as [
            number,
            number
          ]
        }
      }));
  }, [initialMarkers, search.providers]);

  const { selectedProviderId, setSelectedProviderId } = useProviderSelection(
    search.providers,
    initialProviders[0]?.id ?? null
  );

  const handleApplyFilters = useCallback(
    (partial: Partial<Omit<NormalizedCatalogSearchFilters, 'page'>>) => {
      applyAndNavigate(partial);
    },
    [applyAndNavigate]
  );

  const handleMapSelect = useCallback(
    (marker: ProviderMarker) => {
      if (!hasProviderId(marker)) return;

      const profileUrl = getProviderProfileUrl({
        slug: marker.providerSlug,
        address: marker.provinceSlug
          ? { state: marker.provinceSlug }
          : undefined
      });
      if (profileUrl) {
        router.push(profileUrl);
      }
    },
    [router]
  );

  return {
    isHeaderExpanded,
    setHeaderExpanded,
    search,
    providerMarkers,
    selectedProviderId,
    setSelectedProviderId,
    handleApplyFilters,
    handleMapSelect,
    defaultMarkerZoom: DEFAULT_MARKER_ZOOM
  };
}
